import { useState, useCallback, useEffect } from "react";

export interface CoverTransform {
    scale: number;
    offsetX: number;
    offsetY: number;
}

function computeCoverTransform(
    containerW: number,
    containerH: number,
    imageW: number,
    imageH: number,
    anchorX = 0.5,
    anchorY = 0.5,
): CoverTransform {
    const containerRatio = containerW / containerH;
    const imageRatio = imageW / imageH;

    const scale =
        containerRatio > imageRatio
            ? containerW / imageW
            : containerH / imageH;

    const overflowX = imageW * scale - containerW;
    const overflowY = imageH * scale - containerH;

    return {
        scale,
        offsetX: -overflowX * anchorX,
        offsetY: -overflowY * anchorY,
    };
}

/**
 * Computes the transform needed to replicate object-fit:cover behavior
 * for a given image size within a container element.
 *
 * Returns { scale, offsetX, offsetY } to apply on an inner canvas
 * sized to the original image dimensions.
 */
export function useCoverTransform(
    containerRef: React.RefObject<HTMLElement | null>,
    imageW: number,
    imageH: number,
    anchorX = 0.5,
    anchorY = 0.5,
): CoverTransform {
    const [transform, setTransform] = useState<CoverTransform>({
        scale: 1,
        offsetX: 0,
        offsetY: 0,
    });

    const recalc = useCallback(() => {
        const el = containerRef.current;
        if (!el) return;

        setTransform(
            computeCoverTransform(
                el.clientWidth,
                el.clientHeight,
                imageW,
                imageH,
                anchorX,
                anchorY,
            ),
        );
    }, [containerRef, imageW, imageH, anchorX, anchorY]);

    useEffect(() => {
        recalc();

        const ro = new ResizeObserver(recalc);
        if (containerRef.current) ro.observe(containerRef.current);
        return () => ro.disconnect();
    }, [recalc, containerRef]);

    return transform;
}